import { normalizeLocality, type ResponseLocalityData, type LocalityData } from 'service/locality/model';
import { normalizeOfferUser, type ResponseOfferUser, type UserData } from 'service/user/model';
import { normalizeOfferImage, type ResponseOfferImage, type OfferImage } from 'service/image/model';
import { normalizeOfferVideo, type ResponseOfferVideo, type OfferVideo } from 'service/video/model';
import { normalizeOfferPremiseData, type ResponseOfferPremiseData, type PremiseData } from 'service/premise/model';
import {
	normalizeOfferCategoryData,
	type ResponseOfferCategoryData,
	type OfferCategoryData,
} from 'service/category/model';

export type OfferStatus = 'active' | 'inactive' | 'deleted' | 'disabled';

export type ApiOfferStatistics = {
	detail_view: number | null | undefined;
};

export type OfferStatistics = {
	detailView: number | null | undefined;
};

export type OfferLocation = {
	id: number;
	locality: LocalityData;
	main: boolean;
};

export type ApprovalAdmin = 'unchecked' | 'approved' | 'rejected';

export type ApiOfferData = {
	approval_admin: undefined | ApprovalAdmin;
	category: ResponseOfferCategoryData;
	create_date: string;
	deactivation_reason: undefined | null | string;
	deal_type: undefined | string;
	description: null | string;
	dont_show_phone: undefined | boolean;
	edit_date: `${number}-${number}-${number}T${number}:${number}:${number}`;
	id: number;
	images: undefined | ResponseOfferImage[];
	is_advert_mirroring: undefined | boolean;
	is_reserved: undefined | boolean;
	locality: ResponseLocalityData;
	localities:
		| undefined
		| {
				id: number;
				locality: ResponseLocalityData;
				main: boolean;
		  }[];

	manufacturer_cb: undefined | { name: string; seo_name: string; value: number };
	mirroring_id: undefined | null | number;
	model_cb: undefined | { name: string; seo_name: string; value: number };
	name: string;
	old_id: null | number;
	other_localities: undefined | ResponseLocalityData[];
	other_localities_count: undefined | number;
	phone: string;
	premise: undefined | null | ResponseOfferPremiseData;
	preferred_offer: undefined | boolean;
	price: number;
	price_by_agreement: boolean;
	price_original: undefined | number;
	recommendation_data: undefined | string;
	sauto_more_seo_url: undefined | string;
	seo_name: string;
	sold: undefined | WhereSold;
	sorting_date: string;
	source: string;
	status: OfferStatus;
	statistic: undefined | ApiOfferStatistics;
	topped: boolean;
	user: undefined | ResponseOfferUser;
	valid_to: undefined | string;
	videos: undefined | ResponseOfferVideo[];
	videos_total_count: number;
};

export type OfferData = {
	approvalAdmin: undefined | ApprovalAdmin;
	category: OfferCategoryData;
	createDate: string;
	deactivationReason: undefined | null | string;
	dealType: undefined | string;
	description: null | string;
	dontShowPhone: undefined | boolean;
	editDate: string;
	id: number;
	images: undefined | OfferImage[];
	isOfferMirroring: undefined | boolean;
	isReserved: undefined | boolean;
	locality: undefined | LocalityData;
	localities: undefined | OfferLocation[];
	manufacturerCb: undefined | { name: string; seoName: string; value: number };
	mirroringId: undefined | null | number;
	modelCb: undefined | { name: string; seoName: string; value: number };
	name: string;
	oldId: null | number;
	otherLocalities: undefined | LocalityData[];
	otherLocalitiesCount: undefined | number;
	phone: string;
	premise: undefined | null | PremiseData;
	preferredOffer: undefined | boolean;
	price: number;
	priceByAgreement: boolean;
	priceOriginal: undefined | number;
	recommendationData: undefined | string;
	sautoMoreSeoUrl: undefined | string;
	seoName: string;
	sold: undefined | WhereSold;
	sortingDate: string;
	source: string;
	statistics?: undefined | OfferStatistics;
	status: OfferStatus;
	topped: boolean;
	user: undefined | UserData;
	validTo: undefined | string;
	videos: undefined | OfferVideo[];
	videosTotalCount: number;
	loadedOnPageNumber?: number;
};

export type DraftOfferData = Partial<OfferData> & {
	termsOfUse?: boolean;
	email?: string;
};

export function normalizeOfferData(offer: ApiOfferData): OfferData {
	const locality = offer.locality && normalizeLocality(offer.locality);
	const otherLocalities = offer.other_localities?.map((locality) => normalizeLocality(locality));

	return {
		approvalAdmin: offer.approval_admin,
		category: normalizeOfferCategoryData(offer.category),
		createDate: offer.create_date,
		deactivationReason: offer.deactivation_reason,
		dealType: offer.deal_type,
		description: offer.description,
		dontShowPhone: offer.dont_show_phone,
		editDate: offer.edit_date,
		id: offer.id,
		images: offer.images?.map((image) => normalizeOfferImage(image)),
		isOfferMirroring: offer.is_advert_mirroring,
		isReserved: offer.is_reserved,
		locality,
		localities: offer.localities && normalizeLocalities(offer.localities),
		manufacturerCb: offer.manufacturer_cb && {
			name: offer.manufacturer_cb.name,
			seoName: offer.manufacturer_cb.seo_name,
			value: offer.manufacturer_cb.value,
		},
		mirroringId: offer.mirroring_id,
		modelCb: offer.model_cb && {
			name: offer.model_cb.name,
			seoName: offer.model_cb.seo_name,
			value: offer.model_cb.value,
		},
		name: offer.name,
		oldId: offer.old_id,
		otherLocalities,
		otherLocalitiesCount: offer.other_localities_count,
		phone: offer.phone,
		premise: offer.premise && normalizeOfferPremiseData(offer.premise),
		preferredOffer: offer.preferred_offer,
		price: offer.price,
		priceByAgreement: offer.price_by_agreement,
		priceOriginal: offer.price_original,
		recommendationData: offer.recommendation_data,
		sautoMoreSeoUrl: offer.sauto_more_seo_url,
		sold: offer.sold,
		seoName: offer.seo_name,
		sortingDate: offer.sorting_date,
		source: offer.source,
		status: offer.status,
		statistics: offer.statistic && { detailView: offer.statistic.detail_view },
		topped: offer.topped,
		user: offer.user ? normalizeOfferUser(offer.user) : undefined,
		validTo: offer.valid_to,
		videos: offer.videos?.map((video) => normalizeOfferVideo(video)),
		videosTotalCount: offer.videos_total_count,
	};
}

export type WhereSold = 'not_specified' | 'sold_sbazar' | 'sold_other';

function normalizeLocalities(localities: ApiOfferData['localities']) {
	return localities?.map((locality) => ({
		id: locality.id,
		locality: normalizeLocality(locality.locality),
		main: locality.main,
	}));
}

export function createBackupLocalities(
	locality?: LocalityData,
	otherLocalities?: LocalityData[],
): undefined | Partial<OfferLocation>[] {
	return locality
		? [locality].concat(otherLocalities ?? []).map((locality) => ({
				id: undefined,
				locality: locality,
				main: undefined,
			}))
		: undefined;
}

export function isOfferAbleToTop(offer: Partial<OfferData>) {
	return offer.status === 'active' && offer.approvalAdmin !== 'rejected';
}
